// How this device writes Armenian: with tiles, or with a keyboard, and which
// one. A device preference, not an account one: a learner's phone may have an
// Armenian keyboard while their laptop does not. Its own key rather than a
// field of DevicePrefs, which is read before sign-in and knows nothing of
// lessons. An async port for the same reason as prefs.ts: a native app keeps
// it in AsyncStorage.
import { createStore, type StoreApi } from "zustand/vanilla";

export type WriteWith = "tiles" | "keys";
/** `app` is the Aybuben keyboard drawn on screen; `device` is the system's own. */
export type KeysKind = "app" | "device";

export interface WritingPrefs {
	write: WriteWith;
	keys: KeysKind;
}

export interface WritingStore {
	load(): Promise<WritingPrefs>;
	save(p: WritingPrefs): Promise<void>;
}

export const WRITING_KEY = "aybuben:writing";

/** Tiles until the learner asks for a keyboard; the in-app one first, since it needs no setup. */
export const DEFAULT_WRITING: WritingPrefs = { write: "tiles", keys: "app" };

/** Field by field, so one corrupt value does not cost the other. */
export function parseWriting(raw: string | null): WritingPrefs {
	if (!raw) return DEFAULT_WRITING;
	let x: unknown;
	try {
		x = JSON.parse(raw);
	} catch {
		return DEFAULT_WRITING;
	}
	if (typeof x !== "object" || x === null) return DEFAULT_WRITING;
	// Narrowed to a non-null object above; each field is re-checked before use.
	const { write, keys } = x as { write?: unknown; keys?: unknown };
	return {
		write: write === "tiles" || write === "keys" ? write : DEFAULT_WRITING.write,
		keys: keys === "app" || keys === "device" ? keys : DEFAULT_WRITING.keys,
	};
}

/** Degrades to the defaults when storage is blocked (private mode, site data off). */
export const localWritingStore: WritingStore = {
	async load() {
		let raw: string | null = null;
		try {
			raw = globalThis.localStorage?.getItem(WRITING_KEY) ?? null;
		} catch {
			/* nothing remembered */
		}
		return parseWriting(raw);
	},
	async save(p) {
		try {
			globalThis.localStorage?.setItem(WRITING_KEY, JSON.stringify(p));
		} catch {
			/* remembered for this session only */
		}
	},
};

export interface WritingState {
	prefs: WritingPrefs;
	setWrite(w: WriteWith): void;
	setKeys(k: KeysKind): void;
}

/**
 * The device's choice, live: tiles until the store answers (a few
 * milliseconds after launch, long before a lesson can open), and a choice
 * made before it answers is not overwritten by it. `onChange` hears the
 * learner's changes, not the load.
 */
export function createWritingState(
	store: WritingStore,
	onChange?: (p: WritingPrefs) => void,
): StoreApi<WritingState> {
	let touched = false;
	const state = createStore<WritingState>()((set, get) => {
		const change = (next: WritingPrefs) => {
			touched = true;
			set({ prefs: next });
			void store.save(next);
			onChange?.(next);
		};
		return {
			prefs: DEFAULT_WRITING,
			setWrite: (write) => {
				const { prefs } = get();
				if (write !== prefs.write) change({ ...prefs, write });
			},
			setKeys: (keys) => {
				const { prefs } = get();
				if (keys !== prefs.keys) change({ ...prefs, keys });
			},
		};
	});
	void store.load().then((p) => {
		if (!touched) state.setState({ prefs: p });
	});
	return state;
}
